import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { groupTripApi } from '../../api/groupTripApi';
import { settlementApi } from '../../api/settlementApi';
import { opaqueId } from './groupTripModel';
import {
  bookingPresentation, caseReference, formatDisruptionTime, reasonLabel,
} from './disruptionModel';
import SettlementConsequenceEditor from './SettlementConsequenceEditor';
import {
  actionSignature, createSettlementKeyStore, emptyConsequenceDraft, normalizeSettlementProposal, settlementError,
  settlementPath, settlementStatusLabel, toConsequenceRequests, validateConsequenceDrafts,
} from './settlementModel';
import { useSettlementDisplayContext } from './useSettlementDisplayContext';
import './settlement.css';

const ACTIVE = new Set(['DRAFT', 'AWAITING_CONFIRMATIONS', 'READY_TO_RECORD', 'REVISION_REQUIRED']);

/** 只有供應商已確認且確實受影響的訂位才提供建立入口；最終仍由後端驗證 D4 影響。 */
function creatableBookingIds(disruptionCase) {
  return (disruptionCase?.components || [])
    .filter((component) => component.supplierConfirmationStatus === 'CONFIRMED'
      && !['NO_IMPACT', 'UNKNOWN_PENDING_CONFIRMATION'].includes(component.impactScope))
    .map((component) => opaqueId(component.bookingId))
    .filter(Boolean);
}

export default function SettlementProposalsPage() {
  const { groupTripId } = useParams();
  const [searchParams] = useSearchParams();
  const caseId = opaqueId(searchParams.get('caseId'));
  const navigate = useNavigate();
  const display = useSettlementDisplayContext(groupTripId);
  const [items, setItems] = useState([]);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState({ message: '', code: null });
  const [disruptionCase, setDisruptionCase] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const [drafts, setDrafts] = useState([emptyConsequenceDraft()]);
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);
  const keys = useRef(createSettlementKeyStore());

  const load = useCallback(async () => {
    setPhase('loading');
    try {
      const payload = await settlementApi.list(groupTripId);
      setItems((Array.isArray(payload) ? payload : []).map(normalizeSettlementProposal));
      setPhase('ready');
    } catch (cause) {
      setError(settlementError(cause)); setPhase('error');
    }
  }, [groupTripId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!caseId) { setDisruptionCase(null); return undefined; }
    let active = true;
    groupTripApi.getDisruptionCase(groupTripId, caseId)
      .then((value) => { if (active) setDisruptionCase(value || null); })
      .catch(() => { if (active) setDisruptionCase(null); });
    return () => { active = false; };
  }, [groupTripId, caseId]);

  const visible = useMemo(() => (caseId ? items.filter((item) => item.caseId === caseId) : items), [items, caseId]);
  const creatable = useMemo(() => {
    const busyBookings = new Set(items.filter((item) => item.caseId === caseId && ACTIVE.has(item.status)).map((item) => item.bookingId));
    return creatableBookingIds(disruptionCase).filter((id) => !busyBookings.has(id));
  }, [disruptionCase, items, caseId]);
  const selectedBooking = display.bookingsById.get(bookingId);

  async function submitCreate(event) {
    event.preventDefault();
    if (busy) return;
    const result = validateConsequenceDrafts(drafts);
    setValidation(result);
    if (!bookingId || !result.valid) return;
    const body = { caseId, bookingId, consequences: toConsequenceRequests(drafts) };
    const signature = actionSignature('create', null, body);
    setBusy(true); setError({ message: '', code: null });
    try {
      const created = normalizeSettlementProposal(await settlementApi.create(groupTripId, body, keys.current.keyFor(signature)));
      keys.current.release(signature);
      navigate(settlementPath(groupTripId, created.proposalId));
    } catch (cause) {
      const failure = settlementError(cause);
      if (failure.definitive) keys.current.release(signature);
      setError(failure);
      if (failure.reload || failure.code === 95153) await load();
    } finally { setBusy(false); }
  }

  const backLink = caseId
    ? <Link to={`/group-trips/${groupTripId}/disruptions/${caseId}`}>返回異動案件</Link>
    : <Link to={`/public/group-trips/${groupTripId}`}>返回揪團</Link>;

  return (
    <main className="settlement" aria-labelledby="settlement-list-title">
      <header className="settlement__header">
        <div>
          <p className="settlement__eyebrow">行程異動{caseId ? ` · ${caseReference(caseId)}` : ''}</p>
          <h1 id="settlement-list-title">處理協議</h1>
          <p className="settlement__secondary">記錄相關當事人對行程異動處理方式的協議；不會進行任何退款或付款。</p>
        </div>
        <div className="settlement__actions">
          {backLink}
          {caseId && disruptionCase && creatable.length && !showCreate ? (
            <button type="button" className="settlement__button" onClick={() => setShowCreate(true)}>建立處理協議</button>
          ) : null}
        </div>
      </header>
      {error.message ? <p className="settlement__error" role="alert" data-error-code={error.code ?? undefined}>{error.message}</p> : null}
      {!caseId ? <p className="settlement__hint">如需建立新的處理協議，請從行程異動案件進入。</p> : null}
      {phase === 'loading' ? <p className="settlement__state" role="status">載入處理協議中…</p> : null}
      {phase === 'error' ? <div className="settlement__state"><button type="button" className="settlement__button" onClick={load}>重新載入</button></div> : null}
      {phase === 'ready' && visible.length === 0 ? <p className="settlement__state">目前沒有處理協議。</p> : null}
      {phase === 'ready' && visible.length ? (
        <ul className="settlement__list">
          {visible.map((item) => {
            const booking = bookingPresentation(item.bookingId, display.bookingsById);
            return (
              <li key={item.proposalId} className="settlement__card" data-status={item.status}>
                <Link to={settlementPath(groupTripId, item.proposalId)}>
                  <div className="settlement__card-title">
                    <strong>{booking.primary}的處理協議</strong>
                    <span className={`settlement__badge status-${item.status.toLowerCase()}`}>{settlementStatusLabel(item.status)}</span>
                  </div>
                  <p>第 {item.generation} 版 · {item.consequences.length} 個協議項目{item.actorConsent.canDecide ? ' · 等待你回覆' : ''}</p>
                  <p className="settlement__secondary">{booking.reference} · {caseReference(item.caseId)}</p>
                  <time className="settlement__secondary">更新於 {formatDisruptionTime(item.updatedAt || item.createdAt)}</time>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
      {showCreate ? (
        <form className="settlement__panel" onSubmit={submitCreate} aria-labelledby="settlement-create-title" noValidate>
          <h2 id="settlement-create-title">建立處理協議</h2>
          <p className="settlement__secondary">
            {disruptionCase?.reasonDetail || reasonLabel(disruptionCase?.reason)}。建立後為草稿，送出後才會請相關當事人確認。
          </p>
          <label htmlFor="settlement-create-booking">受影響訂位
            <select id="settlement-create-booking" value={bookingId} disabled={busy}
              aria-invalid={validation && !bookingId ? 'true' : undefined}
              aria-describedby={validation && !bookingId ? 'settlement-create-booking-error' : undefined}
              onChange={(event) => setBookingId(event.target.value)}>
              <option value="">請選擇</option>
              {creatable.map((id) => {
                const booking = bookingPresentation(id, display.bookingsById);
                return <option key={id} value={id}>{booking.primary}（{booking.reference}）</option>;
              })}
            </select>
          </label>
          {validation && !bookingId ? <span className="settlement__field-error" id="settlement-create-booking-error">請選擇受影響訂位。</span> : null}
          <SettlementConsequenceEditor idPrefix="settlement-create" drafts={drafts} onChange={setDrafts} errors={validation}
            beneficiaries={selectedBooking?.beneficiaries} travelersById={display.travelersById} disabled={busy} />
          <div className="settlement__actions">
            <button type="submit" className="settlement__button" disabled={busy}>{busy ? '建立中…' : '建立協議草稿'}</button>
            <button type="button" className="settlement__button settlement__button--secondary" disabled={busy} onClick={() => setShowCreate(false)}>取消</button>
          </div>
        </form>
      ) : null}
    </main>
  );
}
